//src/pages/onboarding/index.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  MenuItem,
  Switch
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createHospitalOrganization,
  createHospitalAdminUser,
  getAllHospitalOrganizations,
  checkHospitalEmailExists,
  checkHospitalPhoneExists,
  checkHospitalNameExists,
  updateHospitalOrganizationStatus,
} from "../../services/hospitalOrganizationService";
import {
  createSubscription,
  processPayment,
  initializeOrganizationDatabase,
  getSubscriptionPackages,
} from "../../services/subscriptionService";
import subscriptionHistoryService from "../../services/subscriptionHistoryService";
import { registerPortalUser, checkAdminEmailExists, checkAdminMobileExists } from "../../services/userManagementService";
import axios from "axios";
import { BACKEND_URL } from "../../utils/envConfig";
import { toast } from "react-toastify";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  ONBOARDING_REVIEW_CONTENT_SX,
  FORM_PAGE_PAPER_SX,
} from "../../utils/formModalStyles";
import FormSectionHeader from "../../components/FormSectionHeader";
import dayjs from "dayjs";

const steps = ["Hospital Details", "Review & Confirm"];

// List of Indian States and Union Territories
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const OnboardingPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState(null);
  const [includeGST, setIncludeGST] = useState(false);
  const [gstPercentage, setGstPercentage] = useState("");
  const [packageDetails, setPackageDetails] = useState(null);

  // OPD Consultation Follow-up Waiver
  const [enableOpdConsultationWaiver, setEnableOpdConsultationWaiver] =
    useState(true);

  const [opdConsultationWaiverDays, setOpdConsultationWaiverDays] =
    useState(15);

  const navigate = useNavigate();

  const selectedPackage = sessionStorage.getItem("selectedPackage") || "basic";
  const packageType = sessionStorage.getItem("packageType") || "YEARLY"; // MONTHLY, QUARTERLY, YEARLY
  const paymentMethod = sessionStorage.getItem("paymentMethod") || "online";
  
  // Get cash payment details from sessionStorage (if cash payment)
  const getCashPaymentDetails = () => {
    if (paymentMethod === "cash") {
      const cashPaymentDetailsStr = sessionStorage.getItem("cashPaymentDetails");
      if (cashPaymentDetailsStr) {
        try {
          return JSON.parse(cashPaymentDetailsStr);
        } catch (e) {
          console.error("Error parsing cash payment details:", e);
          return null;
        }
      }
    }
    return null;
  };
  
  // Check if cash payment was authenticated
  useEffect(() => {
    if (paymentMethod === "cash") {
      const cashPaymentAuthenticated = sessionStorage.getItem("cashPaymentAuthenticated");
      if (cashPaymentAuthenticated !== "true") {
        toast.error("Cash payment authentication required. Redirecting to home page.");
        setTimeout(() => {
          navigate("/signup");
        }, 2000);
      }
    }
  }, [paymentMethod, navigate]);

  // Fetch package details on component mount
  useEffect(() => {
    const fetchPackageDetails = async () => {
      try {
        const { packages } = await getSubscriptionPackages();
        const currentPackage = packages.find(
          (pkg) => pkg.id === selectedPackage || pkg.name.toLowerCase() === selectedPackage
        );
        if (currentPackage) {
          setPackageDetails(currentPackage);
        }
      } catch (error) {
        console.error("Error fetching package details:", error);
      }
    };
    fetchPackageDetails();
  }, [selectedPackage]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Organization name is required")
      .matches(/^[a-zA-Z0-9\s\-'.,&()]+$/, "Hospital name must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation")
      .test("has-letter", "Hospital name must contain at least one letter (not just numbers)", (value) => {
        if (!value) return true; // Let required handle empty values
        return /[a-zA-Z]/.test(value); // Must contain at least one letter
      }),
    email: Yup.string()
      .nullable()
      .notRequired()
      .email("Invalid email address"),
    phone: Yup.string()
      .matches(/^\d{10}$/, "Phone must be 10 digits")
      .required("Phone is required"),
    address: Yup.string().required("Address is required"),
    city: Yup.string()
      .required("City is required")
      .matches(/^[a-zA-Z0-9\s\-'.,&()]+$/, "City must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation")
      .test("has-letter", "City must contain at least one letter (not just numbers)", (value) => {
        if (!value) return true; // Let required handle empty values
        return /[a-zA-Z]/.test(value); // Must contain at least one letter
      }),
    state: Yup.string().required("State is required"),
    pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Pincode is required"),
    adminName: Yup.string()
      .required("Admin name is required")
      .matches(/^[a-zA-Z0-9\s\-'.,&()]+$/, "Admin name must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation")
      .test("has-letter", "Admin name must contain at least one letter (not just numbers)", (value) => {
        if (!value) return true; // Let required handle empty values
        return /[a-zA-Z]/.test(value); // Must contain at least one letter
      }),
    adminEmail: Yup.string()
      .email("Invalid email address")
      .required("Admin email is required"),
    adminMobile: Yup.string()
      .matches(/^\d{10}$/, "Mobile must be 10 digits")
      .required("Admin mobile is required"),
  });
  
  // GST validation - optional, only validate if GST is enabled and percentage is provided
  const validateGST = () => {
    if (includeGST && gstPercentage && gstPercentage.trim() !== "") {
      const gstValue = parseFloat(gstPercentage);
      if (isNaN(gstValue) || gstValue < 0 || gstValue > 100) {
        return "GST percentage must be between 0 and 100";
      }
    }
    return null;
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      adminName: "",
      adminEmail: "",
      adminMobile: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (activeStep === 0) {
        // Step 0: Validate hospital details and GST, then move to review
        const gstError = validateGST();
        if (gstError) {
          toast.error(gstError);
          return;
        }
        handleNext();
      } else {
        // Step 1: Submit the form (review step)
        // Validate GST before submission
        const gstError = validateGST();
        if (gstError) {
          toast.error(gstError);
          return;
        }
        await handleSubmit(values);
      }
    },
  });

  // Function to check for duplicate values
  const checkDuplicates = async (values) => {
    const errors = {};
    
    try {
      // Check hospital organization name
      if (values.name && values.name.trim() !== "") {
        try {
          const hospitalNameExists = await checkHospitalNameExists(values.name);
          if (hospitalNameExists) {
            errors.name =
              "A hospital organization with this name already exists";
          }
        } catch (error) {
          console.error("Error checking hospital name:", error);
        }
      }

      // Check hospital email (only if provided)
      if (values.email && values.email.trim() !== "") {
        try {
          const hospitalEmailExists = await checkHospitalEmailExists(values.email);
          if (hospitalEmailExists) {
            errors.email = "This email is already registered with another hospital organization";
          }
        } catch (error) {
          console.error("Error checking hospital email:", error);
          // Continue with other checks even if this one fails
        }
      }
      
      // Check hospital phone
      try {
        const hospitalPhoneExists = await checkHospitalPhoneExists(values.phone);
        if (hospitalPhoneExists) {
          errors.phone = "This phone number is already registered with another hospital organization";
        }
      } catch (error) {
        console.error("Error checking hospital phone:", error);
        // Continue with other checks even if this one fails
      }
      
      // Check admin email
      try {
        console.log(`[checkDuplicates] Checking admin email: ${values.adminEmail}`);
        const adminEmailExists = await checkAdminEmailExists(values.adminEmail);
        console.log(`[checkDuplicates] Admin email exists result: ${adminEmailExists}`);
        if (adminEmailExists) {
          errors.adminEmail = "This email is already registered with another user";
          console.log(`[checkDuplicates] ✅ Admin email duplicate found: ${values.adminEmail}`);
        }
      } catch (error) {
        console.error("[checkDuplicates] Error checking admin email:", error);
        console.error("[checkDuplicates] Error details:", error.response?.data);
        // If endpoint doesn't exist, skip this check - backend will catch duplicates
        // Continue with other checks
      }
      
      // Check admin mobile
      try {
        console.log(`[checkDuplicates] Checking admin mobile: ${values.adminMobile}`);
        const adminMobileExists = await checkAdminMobileExists(values.adminMobile);
        console.log(`[checkDuplicates] Admin mobile exists result: ${adminMobileExists}`);
        if (adminMobileExists) {
          errors.adminMobile = "This mobile number is already registered with another user";
          console.log(`[checkDuplicates] ✅ Admin mobile duplicate found: ${values.adminMobile}`);
        }
      } catch (error) {
        console.error("[checkDuplicates] Error checking admin mobile:", error);
        console.error("[checkDuplicates] Error details:", error.response?.data);
        // If endpoint doesn't exist, skip this check - backend will catch duplicates
        // Continue with other checks
      }
      
      // Set errors in formik if any duplicates found
      if (Object.keys(errors).length > 0) {
        Object.keys(errors).forEach((key) => {
          formik.setFieldError(key, errors[key]);
        });
        toast.error("Please fix the duplicate values before proceeding");
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error checking duplicates:", error);
      toast.error("Error validating data. Please try again.");
      return false;
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate hospital details first
      const validationErrors = await formik.validateForm();
      
      if (Object.keys(validationErrors).length > 0) {
        return; // Don't proceed if there are validation errors
      }
      
      // Check for duplicates before proceeding
      setLoading(true);
      try {
        const noDuplicates = await checkDuplicates(formik.values);
        if (noDuplicates) {
          setActiveStep(activeStep + 1);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // Navigate back to subscription package selection page (HomePage)
      navigate("/signup");
    } else {
      setActiveStep(activeStep - 1);
    }
  };


  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Step 1: Create hospital organization
      const organizationData = {
        name: values.name,
        email: values.email && values.email.trim() !== "" ? values.email.trim() : null, // Optional email
        phone: values.phone,
        address: values.address,
        city: values.city,
        state: values.state,
        pincode: values.pincode,
      };

      let organization;
      try {
        organization = await createHospitalOrganization(organizationData);
      } catch (orgError) {
        console.error("Error creating hospital organization:", orgError);
        console.error("Error response:", orgError.response?.data);
        console.error("Error status:", orgError.response?.status);
        console.error("Request payload:", organizationData);
        
        // Extract error message from various possible formats
        let errorMessage = "Failed to create hospital organization. Please check the details and try again.";
        
        if (orgError.response?.data) {
          const errorData = orgError.response.data;
          
          // Try different error message formats
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            // Handle validation errors array
            errorMessage = errorData.errors.map(err => err.message || err).join(", ");
          } else if (errorData.errorMessage) {
            errorMessage = errorData.errorMessage;
          }
        } else if (orgError.message) {
          errorMessage = orgError.message;
        }
        
        toast.error(errorMessage, { autoClose: 8000 });
        setLoading(false);
        return; // Stop execution here
      }
      
      const organizationId = organization.id;

      // Step 2: Create subscription onboarding using subscription history
      // Try new subscription history endpoint first, fallback to old endpoint if it fails
      let subscriptionHistory = null;
      try {
        const packagePrice = getPackagePrice(selectedPackage);
        // Calculate price based on package type (monthly, quarterly, yearly)
        let calculatedPrice = packagePrice;
        switch (packageType.toUpperCase()) {
          case "QUARTERLY":
            calculatedPrice = packagePrice * 3;
            break;
          case "YEARLY":
            calculatedPrice = packagePrice * 12;
            break;
          case "MONTHLY":
          default:
            calculatedPrice = packagePrice;
            break;
        }
        
        const onboardingData = {
          organizationId: organizationId,
          subscriptionType: selectedPackage.toUpperCase(), // BASIC, INTERMEDIATE, PREMIUM
          packageType: packageType.toUpperCase(), // MONTHLY, QUARTERLY, YEARLY
          amount: calculatedPrice,
          paymentMethod: paymentMethod.toUpperCase(), // ONLINE or CASH
          paymentStatus: paymentMethod.toUpperCase() === "CASH" ? "COMPLETED" : undefined, // Set to COMPLETED for cash payments
          notes: "Initial onboarding",
          // OPD Consultation Follow-up Waiver settings
          enableOpdConsultationWaiver: enableOpdConsultationWaiver,
          opdConsultationWaiverDays: enableOpdConsultationWaiver
            ? Number(opdConsultationWaiverDays)
            : null,
        };
        
        // Add GST percentage if provided (for future use, not for calculation)
        if (includeGST && gstPercentage) {
          onboardingData.includeGST = true;
          onboardingData.gstPercentage = parseFloat(gstPercentage);
        }
        
        // Add payer and recipient details for CASH payment method
        if (paymentMethod.toUpperCase() === "CASH") {
          const cashPaymentDetails = getCashPaymentDetails();
          if (cashPaymentDetails) {
            onboardingData.payerName = cashPaymentDetails.payerName || null;
            onboardingData.payerMobile = cashPaymentDetails.payerMobile || null;
            onboardingData.recipientName = cashPaymentDetails.recipientName || null;
            onboardingData.recipientMobile = cashPaymentDetails.recipientMobile || null;
            // Optional comment (also kept on notes for older backends that only store notes)
            if (cashPaymentDetails.paymentComment) {
              onboardingData.paymentComment = cashPaymentDetails.paymentComment;
              onboardingData.notes = `Initial onboarding. Payment note: ${cashPaymentDetails.paymentComment}`;
            }
          }
        }
        
        subscriptionHistory = await subscriptionHistoryService.createSubscriptionOnboarding(onboardingData);
        console.log("Subscription history created successfully:", subscriptionHistory);

        // Persist optional cash comment + proof via cash-details endpoint (after create)
        if (paymentMethod.toUpperCase() === "CASH" && subscriptionHistory?.id) {
          const cashPaymentDetails = getCashPaymentDetails();
          if (cashPaymentDetails) {
            try {
              await subscriptionHistoryService.updateSubscriptionHistoryCashDetails(
                subscriptionHistory.id,
                cashPaymentDetails
              );
            } catch (cashDetailsError) {
              console.error("Error updating cash payment comment/proof:", cashDetailsError);
              toast.warning(
                "Subscription created but payment comment/proof could not be saved. Please contact support.",
                { autoClose: 5000 }
              );
            }
          }
        }
        
        // Update subscription history status based on payment method
        // Cash payment: Status remains ACTIVE (default)
        // Online payment: Set status to INACTIVE (payment pending)
        if (paymentMethod.toUpperCase() === "ONLINE" && subscriptionHistory?.id) {
          try {
            await subscriptionHistoryService.updateSubscriptionHistoryStatus(
              subscriptionHistory.id,
              "INACTIVE"
            );
            console.log("Subscription history status set to INACTIVE for online payment");
          } catch (statusError) {
            console.error("Error updating subscription history status:", statusError);
            // Don't fail onboarding if status update fails - it's not critical
            toast.warning("Subscription created but status update failed. Please contact support.", {
              autoClose: 5000
            });
          }
        }
      } catch (historyError) {
        console.warn("Subscription history creation failed, using legacy subscription creation:", historyError);
        // Fallback to legacy subscription creation for backward compatibility
        const packageTypeUpper = selectedPackage.toUpperCase();
        const subscriptionData = {
          organizationId: organizationId,
          packageType: packageTypeUpper,
          paymentMethod: paymentMethod.toUpperCase(),
        };

        const subscription = await createSubscription(subscriptionData);

        // Process payment (if online) for legacy subscription
        if (paymentMethod === "online") {
          const basePrice = getPackagePrice(selectedPackage);
          let calculatedPrice = basePrice;
          switch (packageType.toUpperCase()) {
            case "QUARTERLY":
              calculatedPrice = basePrice * 3;
              break;
            case "YEARLY":
              calculatedPrice = basePrice * 12;
              break;
            default:
              calculatedPrice = basePrice;
          }
          await processPayment({
            subscriptionId: subscription.id,
            amount: calculatedPrice,
            paymentMethod: "ONLINE",
          });
        }
      }

      // Step 4: Initialize organization database tables
      // This creates organization-specific tables like:
      // - {organizationId}_patient
      // - {organizationId}_emergency_admission
      // - {organizationId}_emergency_details
      // - {organizationId}_staff, {organizationId}_doctor, etc.
      await initializeOrganizationDatabase(organizationId);

      // Step 4.5: Set organization status based on payment method
      // Cash payment: Set to active (authentication already completed)
      // Online payment: Set to inactive (payment pending)
      try {
        const isActive = paymentMethod.toUpperCase() === "CASH";
        await updateHospitalOrganizationStatus(organizationId, isActive);
        console.log(`Organization status set to ${isActive ? "active" : "inactive"} for ${paymentMethod} payment`);
      } catch (statusError) {
        console.error("Error updating organization status:", statusError);
        // Don't fail onboarding if status update fails - it's not critical
        toast.warning("Organization created but status update failed. Please contact support.", {
          autoClose: 5000
        });
      }

      // Step 5: Create admin user for hospital organization
      // Generate a default password for admin user
      // Format: Admin@[first3charsOfName][last4digitsofMobile]
      const defaultPassword = `Admin@${values.adminName.substring(0, 3).toUpperCase()}${values.adminMobile.slice(-4)}`;
      
      let adminUser;
      let createdUserId = null;
      
      try {
        // Step 5a: Register admin user in user management service with organization_id
        const userData = {
          fullName: values.adminName,
          mobile: values.adminMobile,
          password: defaultPassword,
          role: "admin", // Admin role for hospital organization
          email: values.adminEmail.trim(), // Required email
          department: "", // Admin doesn't belong to a department
          organizationId: organizationId, // Include organization ID in user registration
        };

        try {
  const createdUser = await registerPortalUser(userData);

  if (!createdUser || !createdUser.id) {
    throw new Error(
      "User registration succeeded but no user ID was returned."
    );
  }

  createdUserId = createdUser.id;

  console.log(
    "Admin user registered successfully with ID:",
    createdUserId
  );

  console.log(
    "Organization ID sent during registration:",
    organizationId
  );

  console.log(
    "Organization ID returned by user management:",
    createdUser.organization_id
  );

  const registeredOrganizationId =
    createdUser.organization_id ??
    createdUser.organizationId ??
    null;

  if (
    registeredOrganizationId === null ||
    Number(registeredOrganizationId) !== Number(organizationId)
  ) {
    throw new Error(
      `User registration succeeded, but organization_id was not saved correctly. ` +
      `Expected ${organizationId}, received ${registeredOrganizationId}.`
    );
  }

  console.log(
    "Admin user successfully linked to organization:",
    organizationId
  );

} catch (userRegError) {
  console.error("User registration error:", userRegError);

  toast.error(
    `Admin user registration failed: ${
      userRegError.response?.data?.error ||
      userRegError.response?.data?.message ||
      userRegError.message
    }`,
    { autoClose: 8000 }
  );

  return;
}

        // Step 5b: Create admin user record and link to organization via backend
        const adminData = {
          fullName: values.adminName,
          email: values.adminEmail.trim(), // Required email
          mobile: values.adminMobile,
          password: defaultPassword,
          userId: createdUserId, // Include userId if registration was successful
        };

        try {
          adminUser = await createHospitalAdminUser(organizationId, adminData);
          
          // Check if manual creation is required (due to user management service restrictions)
          if (adminUser?.requiresManualCreation) {
            // This is not an error - onboarding can continue
            toast.warning(
              `Organization created successfully! However, admin user creation requires manual setup. ` +
              `Please contact support or create the admin user manually. Admin email: ${adminUser.adminDetails?.email || values.adminEmail}`,
              { autoClose: 8000 }
            );
            
            // Store admin details for manual creation
            sessionStorage.setItem("organizationId", organizationId);
            sessionStorage.setItem("adminDetails", JSON.stringify({
              name: values.adminName,
              email: values.adminEmail,
              mobile: values.adminMobile,
              password: defaultPassword,
              userId: createdUserId, // Store userId even if backend creation failed
              requiresManualCreation: true,
              adminDetails: adminUser.adminDetails,
            }));
          } else {
            // Admin user created successfully
            // IMPORTANT: Always use createdUserId (from user registration) for staff creation
            // adminUser.id might be the admin record id (e.g., 316), not the userId (e.g., 315)
            // The login uses user.id which is the userId from user registration, so we must match that
            
            // Step 5c: Add admin user to staff table (so they can login properly)
            if (createdUserId) {
              try {
                // Create staff record directly via API (user is already registered)
                // Include organizationId and password in staff record
                // CRITICAL: Include password to ensure same password is stored in both user and staff tables
                const staffData = {
                  name: values.adminName,
                  email: values.adminEmail.trim(), // Required email
                  mobile: values.adminMobile,
                  userRole: "admin", // Admin role
                  departmentId: null, // Admin doesn't belong to a department
                  userId: createdUserId, // CRITICAL: Always use the userId from user registration (not adminUser.id)
                  organizationId: organizationId, // CRITICAL: Store organizationId in staff table
                  password: defaultPassword, // CRITICAL: Store same password as in user table
                  joiningDate: dayjs().format("YYYY-MM-DD"), // Set today's date as joining date
                };
                
                console.log("Creating staff record with userId from registration:", createdUserId);
                console.log("Staff data with organizationId:", staffData);
                
                const staffResponse = await axios.post(`${BACKEND_URL}/staff`, staffData);
                
                if (staffResponse.status === 200) {
                  console.log("Admin user added to staff table successfully");
                  console.log("Staff created with userId:", createdUserId);
                  console.log("Staff response:", staffResponse.data);
                }
              } catch (staffError) {
                console.error("Error adding admin to staff table:", staffError);
                console.error("Error response:", staffError.response?.data);
                console.error("Staff creation failed for userId:", createdUserId);
                // Don't fail onboarding if staff creation fails - admin can still login
                // The login flow will handle admin role even if not in staff table
              }
            } else {
              console.warn("Cannot create staff record: createdUserId is null");
            }
            
            sessionStorage.setItem("organizationId", organizationId);
            sessionStorage.setItem("adminDetails", JSON.stringify({
              name: values.adminName,
              email: values.adminEmail,
              mobile: values.adminMobile,
              password: defaultPassword,
              userId: createdUserId, // Always use the userId from user registration
            }));
            
            // Show credentials in popup
            setAdminCredentials({
              mobile: values.adminMobile,
              password: defaultPassword,
            });
            setShowCredentialsModal(true);
          }
        } catch (adminError) {
          // Backend admin creation failed, but user might be registered
          console.error("Backend admin creation error:", adminError);
          
          if (createdUserId) {
            // User was registered successfully, just backend linking failed
            toast.warning(
              `User registered but organization linking failed. User ID: ${createdUserId}. Please contact support.`,
              { autoClose: 8000 }
            );
            
            sessionStorage.setItem("organizationId", organizationId);
            sessionStorage.setItem("adminDetails", JSON.stringify({
              name: values.adminName,
              email: values.adminEmail,
              mobile: values.adminMobile,
              password: defaultPassword,
              userId: createdUserId,
              requiresManualLinking: true,
            }));
          } else {
            // Both user registration and backend creation failed
            if (adminError.response?.status === 200 && adminError.response?.data?.requiresManualCreation) {
              // Handle as manual creation requirement
              const adminUserData = adminError.response.data;
              toast.warning(
                `Organization created successfully! However, admin user creation requires manual setup. ` +
                `Please contact support or create the admin user manually. Admin email: ${adminUserData.adminDetails?.email || values.adminEmail}`,
                { autoClose: 8000 }
              );
              
              sessionStorage.setItem("organizationId", organizationId);
              sessionStorage.setItem("adminDetails", JSON.stringify({
                name: values.adminName,
                email: values.adminEmail,
                mobile: values.adminMobile,
                password: defaultPassword,
                requiresManualCreation: true,
                adminDetails: adminUserData.adminDetails,
              }));
            } else {
              // Real error
              if (adminError.response) {
                console.error("Error response:", adminError.response.data);
                console.error("Error status:", adminError.response.status);
                toast.error(
                  `Admin user creation failed: ${adminError.response.data?.message || adminError.message}. Please contact support.`
                );
              } else {
                toast.error(`Admin user creation failed: ${adminError.message}`);
              }
              console.warn("Continuing onboarding despite admin user creation failure");
            }
          }
        }
      } catch (error) {
        // Catch any unexpected errors
        console.error("Unexpected error in admin creation:", error);
        toast.warning(
          `Admin user creation encountered issues, but organization is created. Please contact support.`,
          { autoClose: 8000 }
        );
        
        // Store what we have
        sessionStorage.setItem("organizationId", organizationId);
        sessionStorage.setItem("adminDetails", JSON.stringify({
          name: values.adminName,
          email: values.adminEmail,
          mobile: values.adminMobile,
          password: defaultPassword,
          userId: createdUserId,
        }));
        
        // Show credentials even if there were issues
        if (createdUserId) {
          setAdminCredentials({
            mobile: values.adminMobile,
            password: defaultPassword,
          });
          setShowCredentialsModal(true);
        } else {
          // Navigate to login if no credentials to show
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        }
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error(
        error.response?.data?.message || "Failed to complete onboarding. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getPackagePrice = (pkg) => {
    const prices = { basic: 999, intermediate: 2499, premium: 4999 };
    return prices[pkg] || 999;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 0.5, ...DENSE_FORM_BODY_SX }}>
            <FormSectionHeader title="Hospital Information" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hospital Name"
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(value)) {
                      formik.setFieldValue("name", value);
                    }
                  }}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hospital Email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[a-zA-Z0-9._+-@]*$/.test(value)) {
                      formik.setFieldValue("email", value);
                    }
                  }}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formik.values.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    formik.setFieldValue("phone", value);
                  }}
                  inputProps={{ maxLength: 10 }}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  multiline
                  rows={2}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formik.values.city}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow alphanumeric, spaces, and common punctuation (hyphens, apostrophes, periods, commas, ampersands, parentheses)
                    if (value === "" || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(value)) {
                      formik.setFieldValue("city", value);
                    }
                  }}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="State"
                  name="state"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  {...DENSE_FIELD_PROPS}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select State</em>
                  </MenuItem>
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formik.values.pincode}
                  onChange={(e) => {
                    // Only allow numeric input and limit to 6 digits
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    formik.setFieldValue("pincode", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                  helperText={formik.touched.pincode && formik.errors.pincode}
                  inputProps={{
                    maxLength: 6,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormSectionHeader title="GST (Goods and Services Tax) Settings" sx={{ mb: 0.5 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeGST}
                      onChange={(e) => {
                        setIncludeGST(e.target.checked);
                        if (!e.target.checked) {
                          setGstPercentage("");
                        }
                      }}
                    />
                  }
                  label="Include GST in subscription (optional)"
                />
                {includeGST && (
                  <TextField
                    fullWidth
                    label="GST Percentage (%)"
                    type="number"
                    value={gstPercentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and one decimal point
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setGstPercentage(value);
                      }
                    }}
                    error={includeGST && gstPercentage && (parseFloat(gstPercentage) < 0 || parseFloat(gstPercentage) > 100)}
                    helperText={
                      includeGST && gstPercentage && (parseFloat(gstPercentage) < 0 || parseFloat(gstPercentage) > 100)
                        ? "GST percentage must be between 0 and 100"
                        : "Enter GST percentage (e.g., 18 for 18%)"
                    }
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 0.01,
                    }}
                    sx={{ mt: 1, maxWidth: 280 }}
                    {...DENSE_FIELD_PROPS}
                  />
                )}
              </Grid>

              <Grid item xs={6}>
              <FormControlLabel
              control={
                <Switch
                  checked={enableOpdConsultationWaiver}
                  onChange={(e) =>
                    setEnableOpdConsultationWaiver(e.target.checked)
                  }
                />
              }
              label="Enable OPD Consultation Follow-up Waiver"
            />

            {enableOpdConsultationWaiver && (
              <TextField
              label="OPD Consultation Follow-up Days"
              type="number"
              value={opdConsultationWaiverDays}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "" || /^\d+$/.test(value)) {
                  setOpdConsultationWaiverDays(value);
                }
              }}
              inputProps={{
                min: 0,
                step: 1,
              }}
              fullWidth
            />
            )}
            </Grid>

              <Grid item xs={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 0.75 }}
                  >
                    OPD Consultation Follow-up Waiver
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45 }}
                  >
                    <strong>Enabled:</strong>{" "}
                    {enableOpdConsultationWaiver ? "Yes" : "No"}
                  </Typography>

                  {enableOpdConsultationWaiver && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.45 }}
                    >
                      <strong>Follow-up Waiver Days:</strong>{" "}
                      {opdConsultationWaiverDays} days
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <FormSectionHeader title="Admin Details" sx={{ mb: 0.5 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin Name"
                  name="adminName"
                  value={formik.values.adminName}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow alphanumeric, spaces, and common punctuation (hyphens, apostrophes, periods, commas, ampersands, parentheses)
                    if (value === "" || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(value)) {
                      formik.setFieldValue("adminName", value);
                    }
                  }}
                  error={formik.touched.adminName && Boolean(formik.errors.adminName)}
                  helperText={formik.touched.adminName && formik.errors.adminName}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin Email"
                  name="adminEmail"
                  type="email"
                  value={formik.values.adminEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[^\s@]*@?[^\s@]*$/.test(value)) {
                      formik.setFieldValue("adminEmail", value);
                    }
                  }}
                  error={formik.touched.adminEmail && Boolean(formik.errors.adminEmail)}
                  helperText={formik.touched.adminEmail && formik.errors.adminEmail}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin Mobile"
                  name="adminMobile"
                  value={formik.values.adminMobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    formik.setFieldValue("adminMobile", value);
                  }}
                  inputProps={{ maxLength: 10 }}
                  error={formik.touched.adminMobile && Boolean(formik.errors.adminMobile)}
                  helperText={formik.touched.adminMobile && formik.errors.adminMobile}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ ...DENSE_FORM_BODY_SX, ...ONBOARDING_REVIEW_CONTENT_SX }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 1.25, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                    Hospital Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    <strong>Name:</strong> {formik.values.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    <strong>Email:</strong> {formik.values.email && formik.values.email.trim() !== "" ? formik.values.email : "Not provided"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    <strong>Phone:</strong> {formik.values.phone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    <strong>Address:</strong> {formik.values.address}, {formik.values.city},{" "}
                    {formik.values.state} - {formik.values.pincode}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 1.25, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }} sx={{ lineHeight: 1.45 }}>
                    GST Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" >
                    Include GST: {includeGST ? "Yes" : "No"}
                  </Typography>
                  {includeGST && gstPercentage && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      GST Percentage: {gstPercentage}% (stored for future use)
                    </Typography>
                  )}
                 
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 0.75 }}
                    sx={{ lineHeight: 1.45 }}
                  >
                    OPD Consultation Follow-up Waiver
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45 }}
                  >
                    <strong>Enabled:</strong>{" "}
                    {enableOpdConsultationWaiver ? "Yes" : "No"}
                  </Typography>

                  {enableOpdConsultationWaiver && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.45 }}
                    >
                      <strong>Follow-up Waiver Days:</strong>{" "}
                      {opdConsultationWaiverDays} days
                    </Typography>
                    )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 1.25, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                    Subscription Package
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{selectedPackage.toUpperCase()}</strong> - {(() => {
                      const basePrice = getPackagePrice(selectedPackage);
                      let price = basePrice;
                      let period = "/month";
                      switch (packageType.toUpperCase()) {
                        case "QUARTERLY":
                          price = basePrice * 3;
                          period = "/quarter";
                          break;
                        case "YEARLY":
                          price = basePrice * 12;
                          period = "/year";
                          break;
                        default:
                          price = basePrice;
                          period = "/month";
                      }
                      return `₹${price.toLocaleString('en-IN')}${period}`;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    <strong>Billing Cycle:</strong> {packageType === "MONTHLY" ? "Monthly" : packageType === "QUARTERLY" ? "Quarterly" : "Yearly"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, mb: 0.5 }}>
                    Payment Method: {paymentMethod === "online" ? "Online Payment" : "Cash Payment"}
                  </Typography>
                  {packageDetails && packageDetails.features && packageDetails.features.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
                        Package Capabilities:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {packageDetails.features.map((feature, index) => (
                          <li key={index}>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                              {feature}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {packageDetails && packageDetails.maxUsers !== undefined && (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, mt: 0.5 }}>
                      <strong>Max Users Allowed:</strong>{" "}
                      {packageDetails.maxUsers === -1 ? "Unlimited" : packageDetails.maxUsers}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 2 }}>
      <Container maxWidth={false} sx={{ px: { xs: 1.5, sm: 2 }, maxWidth: "800px", mx: "auto" }}>
        <Button
          onClick={() => navigate("/login")}
          variant="outlined"
          color="primary"
          sx={{ ...FORM_CANCEL_BUTTON_SX, mb: 1.5 }}
        >
          Back to Home
        </Button>

        <Paper sx={FORM_PAGE_PAPER_SX}>
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{ fontSize: "1.5rem", fontWeight: "bold", pb: 0.5, mb: 0, borderBottom: "1px solid", borderColor: "divider" }}
          >
            Hospital Onboarding
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mt: 1, mb: 1 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box component="form" onSubmit={formik.handleSubmit}>
            {renderStepContent()}

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 1.5, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
              <Button
                disabled={loading}
                onClick={handleBack}
                variant="outlined"
                color="primary"
                sx={FORM_CANCEL_BUTTON_SX}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                sx={FORM_SUBMIT_BUTTON_SX}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  "Complete Onboarding"
                ) : (
                  "Next"
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Credentials Modal */}
      <Dialog
        open={showCredentialsModal}
        onClose={() => {
          // X / backdrop / close: dismiss popup only; stay on onboarding page
          setShowCredentialsModal(false);
        }}
        maxWidth="md"
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={{ ...STANDARD_FORM_DIALOG_TITLE_SX, position: "relative", pr: 6 }}>
          Admin Credentials
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: "normal", fontSize: "0.875rem" }}>
            Please save these credentials before continuing to login.
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setShowCredentialsModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {adminCredentials && (
            <Box>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                    Mobile Number:
                  </Typography>
                  <Typography variant="h6" sx={{ flex: 1, fontWeight: "bold" }}>
                    {adminCredentials.mobile}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(adminCredentials.mobile);
                      toast.success("Mobile number copied to clipboard!");
                    }}
                    sx={{ ml: 1 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                    Password:
                  </Typography>
                  <Typography variant="h6" sx={{ flex: 1, fontWeight: "bold", color: "#1976d2" }}>
                    {adminCredentials.password}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(adminCredentials.password);
                      toast.success("Password copied to clipboard!");
                    }}
                    sx={{ ml: 1 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Please save these credentials securely. You will need them to log in to the system.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button
            onClick={() => {
              setShowCredentialsModal(false);
              navigate("/login");
            }}
            variant="contained"
            color="primary"
            sx={FORM_SUBMIT_BUTTON_SX}
          >
            I've Saved My Credentials - Continue to Login
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnboardingPage;

